import { SupportChatCodeSendStatus } from '../../api/public/enums/supportChatCodeSendStatus';
import { SupportChatCodeVerifyStatus } from '../../api/public/enums/supportChatCodeVerifyStatus';
import { SupportChatEscalationStatus } from '../../api/public/enums/supportChatEscalationStatus';
import { SupportChatEventMatch } from '../../api/public/enums/supportChatEventMatch';
import { SupportChatEventStatus } from '../../api/public/enums/supportChatEventStatus';
import { SupportChatInvoicingStatus } from '../../api/public/enums/supportChatInvoicingStatus';
import { SupportChatLookupStatus } from '../../api/public/enums/supportChatLookupStatus';
import { SupportChatResendStatus } from '../../api/public/enums/supportChatResendStatus';
import type { FlowContext, FlowStep, SupportFlow } from './types';

/**
 * Version 1 flow definitions.
 *
 * NOTE: the six main categories and their sub-steps are a WORKING DRAFT. The
 * final taxonomy is derived from the ~100 real support e-mails (Phase B) — the
 * engine and the widget do not care what the flows look like, so replacing
 * this file is the only change needed.
 *
 * Conventions
 *  - answer fields: moneyLeft, eventId, eventName, orderEmail, contactEmail,
 *    purchaseRange, description, invoiceData, ...
 *  - every escalation branch passes through `contactEmail` → `confirm` →
 *    `escalate` so the support request always carries a valid reply address.
 */

const hasEventContext = (ctx: FlowContext) => ctx.contextEventId != null;
/** The server found the customer's order → it knows the event; the widget must not ask for it again. */
const orderFound = (ctx: FlowContext) =>
	ctx.answers.lookupStatus === SupportChatLookupStatus.PaidOrderFound ||
	ctx.answers.lookupStatus === SupportChatLookupStatus.OrderFoundNotPaid ||
	ctx.answers.lookupStatus === SupportChatLookupStatus.OrderFoundPendingTransfer;
const eventKnown = (ctx: FlowContext) => ctx.answers.eventId != null || !!ctx.answers.eventName || orderFound(ctx);

/**
 * Does a free-text question concern an event? V1 has no language model, so this is a
 * word-stem check (sk/cs/en, diacritics ignored): "kedy otvárate brány", "je vstup od
 * 18 rokov", "parking at the venue" → yes; "ako si vytvorím účet" → no. Owner decision:
 * the bot decides this itself instead of asking the customer.
 */
const EVENT_QUESTION_STEMS = [
	// sk / cs
	'event', 'podujat', 'akci', 'akce', 'festival', 'koncert', 'ples', 'predstaven', 'zapas', 'vstupen', 'listok', 'listk',
	'program', 'bran', 'termin', 'datum', 'parkov', 'sedadl', 'seden', 'sektor', 'kapacit', 'vekov', 'rokov', 'miesto', 'miste',
	'kona', 'zaciat', 'zacin', 'otvar', 'oblec', 'dress', 'ubytov', 'organizator', 'poradatel', 'interpret', 'ucinkuj', 'vystup',
	// en
	'ticket', 'concert', 'show', 'gate', 'doors', 'venue', 'parking', 'seat', 'capacity', 'age', 'schedule', 'lineup', 'start',
];
/** Promo-code answers the support template covers without a human (see promoCode flow). */
const TWO_CODES_STEMS = ['dva kod', 'dve kod', 'dva zlav', 'dve zlav', '2 kod', '2 zlav', 'dva vouch', '2 vouch', 'druhy kod', 'druhy zlav', 'viac kod', 'viacero kod', 'oba kod', 'obidva', 'two code', 'second code', 'both code', 'two vouch', 'two discount'];
const BLOCKED_CODE_STEMS = ['nedokonc', 'nepresla', 'neprosla', 'neprebehla', 'zlyhal', 'selhal', 'znova', 'znovu', 'opat', 'druhykrat', 'druhy krat', 'podruhe', 'blokov', 'payment failed', 'again', 'second time', 'blocked'];
const looksLikeTwoCodes = (text: unknown) => {
	const t = normalizeName(text);
	return TWO_CODES_STEMS.some((stem) => t.includes(stem));
};
const looksLikeBlockedCode = (text: unknown) => {
	const t = normalizeName(text);
	return BLOCKED_CODE_STEMS.some((stem) => t.includes(stem));
};

/** "netýka sa žiadneho eventu", "not about an event" - the customer says there is no event; do not ask for one (QA F-01). */
const EVENT_NEGATION_STEMS = [
	'netyka sa', 'netyka ziadn', 'ziadneho eventu', 'ziadny event', 'ziadnu akci', 'bez eventu', 'nie je o event', 'nesuvisi s event',
	'nema nic s event', 'nejde o event', 'mimo eventu', 'netyka se', 'zadneho eventu', 'zadne akce', 'not about an event', 'no event',
	'not related to an event', 'unrelated to an event', 'not event related',
];
const looksLikeEventQuestion = (text: unknown) => {
	const t = normalizeName(text);
	if (EVENT_NEGATION_STEMS.some((stem) => t.includes(stem))) {
		return false;
	}
	return EVENT_QUESTION_STEMS.some((stem) => t.includes(stem));
};

/** lower-case, diacritics stripped – for cheap client-side name checks only */
const normalizeName = (value: unknown) =>
	String(value ?? '')
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[^a-z0-9]+/g, ' ')
		.trim();

/**
 * Owner decision: "the name on the ticket does not matter at the entrance" is
 * true ONLY for the Silné reči shows; every other event goes to support.
 */
const isSilneReci = (ctx: FlowContext) => normalizeName(ctx.answers.eventName ?? ctx.contextEventName).includes('silne reci');

/**
 * "Organizer contact not found" → straight to the hand-over. The flow itself says what
 * the customer wants (refund / invoice / event question), so nothing more is asked than
 * the reply e-mail.
 */
const noContactSteps = (messageKey: string): FlowStep[] => [
	{ id: 'organizerUnknown', type: 'message', textKey: messageKey, situationKey: 'sit.noOrganizerContact', next: 'escEvent' },
];

/**
 * Mailbox proof (owner decision): before the first lookup under an address the customer
 * types back a code mailed to it - logged-in users included. The lookup step answers
 * `email_not_verified` until then and comes back here; `returnTo` is that lookup step.
 */
const emailCodeSteps = (returnTo: string): FlowStep[] => [
	{
		id: 'sendCode',
		type: 'backendAction',
		action: 'sendCode',
		textKey: 'code.sending',
		// already proved this very address earlier in the conversation → straight to the lookup;
		// never right after the server said this conversation holds no such proof (a replaced
		// conversation after a 410, another tab verified a different address) - skipping then
		// sent the widget straight back to the lookup in an endless loop
		skipIf: (ctx) =>
			!!ctx.answers.orderEmail && ctx.answers.verifiedEmail === ctx.answers.orderEmail && ctx.answers.lookupStatus !== SupportChatLookupStatus.EmailNotVerified,
		next: returnTo,
		// a server-reported failure ends the flow; a network hiccup gets the usual retry prompt
		onResult: { [SupportChatCodeSendStatus.Sent]: 'enterCode', '*': 'codeSendFailed' },
	},
	{
		id: 'enterCode',
		type: 'text',
		field: 'emailCode',
		textKey: 'code.enter',
		placeholderKey: 'code.placeholder',
		maxLength: 6,
		next: 'verifyCode',
	},
	{
		id: 'verifyCode',
		type: 'backendAction',
		action: 'verifyCode',
		textKey: 'code.checking',
		onResult: { [SupportChatCodeVerifyStatus.Verified]: returnTo, [SupportChatCodeVerifyStatus.Invalid]: 'codeInvalid', [SupportChatCodeVerifyStatus.Expired]: 'codeExpired', [SupportChatCodeVerifyStatus.TooManyAttempts]: 'codeExpired', [SupportChatCodeVerifyStatus.NoCode]: 'sendCode', '*': 'codeInvalid' },
		onError: 'codeInvalid',
	},
	{ id: 'codeInvalid', type: 'message', textKey: 'code.invalid', next: 'enterCode' },
	// expired or too many guesses → a fresh code (its own rate limit stops abuse)
	{ id: 'codeExpired', type: 'message', textKey: 'code.expired', next: 'sendCode' },
	{ id: 'codeSendFailed', type: 'end', textKey: 'code.sendFailed', resolution: 'human' },
];

/**
 * Shared tail: (event, when still unknown) → contact e-mail → confirmation →
 * escalate → done. Support wants e-mail + event + problem on every request, so
 * the event is asked here whenever the flow did not need it earlier.
 */
function escalationTail(reason: string, summaryFields: string[], askEvent = true): FlowStep[] {
	const eventAsk: FlowStep[] = askEvent
		? [
				{
					id: 'escEvent',
					type: 'event',
					textKey: 'event.confirmQuestion',
					skipIf: (ctx) => !hasEventContext(ctx) || eventKnown(ctx),
					next: 'escEventName',
				},
				{
					id: 'escEventName',
					type: 'text',
					field: 'eventName',
					textKey: 'event.askNameNeutral',
					placeholderKey: 'event.namePlaceholder',
					maxLength: 120,
					skipIf: eventKnown,
					next: 'contactEmailAsk',
				},
			]
		: [];
	return [
		...eventAsk,
		{
			id: 'contactEmailAsk',
			type: 'choice',
			field: 'contactEmail',
			textKey: 'contact.useOrderEmail',
			skipIf: (ctx) => !ctx.answers.orderEmail,
			next: 'contactEmail',
			options: [
				{ id: 'same', labelKey: 'contact.yesSame', value: '$answers.orderEmail', next: 'confirm' },
				{ id: 'other', labelKey: 'contact.useOther', value: null, next: 'contactEmail' },
			],
		},
		{
			id: 'contactEmail',
			type: 'email',
			field: 'contactEmail',
			textKey: 'contact.askEmail',
			skipIf: (ctx) => !!ctx.answers.contactEmail,
			next: 'confirm',
		},
		{
			id: 'confirm',
			type: 'confirmation',
			textKey: 'escalation.confirmIntro',
			summaryFields,
			next: 'escalate',
			escalationReason: reason,
		},
		{
			id: 'escalate',
			type: 'backendAction',
			action: 'escalate',
			textKey: 'escalation.sending',
			onResult: { [SupportChatEscalationStatus.Created]: 'escalated', [SupportChatEscalationStatus.Duplicate]: 'escalated', '*': 'escalateFailed' },
			escalationReason: reason,
		},
		{
			id: 'escalateFailed',
			type: 'message',
			textKey: 'escalation.failed',
			next: 'escalateFailedEnd',
		},
		{ id: 'escalateFailedEnd', type: 'end', resolution: 'human' },
		{
			id: 'escalated',
			type: 'end',
			textKeys: ['escalation.done', 'escalation.confirmationMail'],
			resolution: 'escalated',
		},
	];
}

/** @param askKey wording of the "which event" question – "bought a ticket for" only fits flows where a purchase exists */
interface EventStepOptions {
	/**
	 * Resolve the typed name on the server and let the customer confirm a fuzzy match
	 * ("Myslíte event X (dátum)?") before anything about the event is handed out
	 * (QA Z-01: "QA Koncert" must not silently become some other concert). A second
	 * "no" hands the event over to support instead of guessing again.
	 */
	confirm?: boolean;
	/** The name may be left empty - "my question is not about an event" (QA F-01). */
	optional?: boolean;
}

const confirmMatchText = (ctx: FlowContext) => (ctx.answers.matchedEventDate ? 'event.confirmMatch' : 'event.confirmMatchNoDate');
const isFuzzyMatch = (ctx: FlowContext) => ctx.answers.eventMatch === SupportChatEventMatch.Fuzzy;

const eventSteps = (next: string | ((ctx: FlowContext) => string), askKey = 'event.askName', opts: EventStepOptions = {}): FlowStep[] => [
	{
		id: 'event',
		type: 'event',
		textKey: 'event.confirmQuestion',
		skipIf: (ctx) => !hasEventContext(ctx) || eventKnown(ctx),
		next: 'eventName',
	},
	{
		id: 'eventName',
		type: 'text',
		field: 'eventName',
		textKey: askKey,
		placeholderKey: opts.optional ? 'event.namePlaceholderOptional' : 'event.namePlaceholder',
		maxLength: 120,
		required: !opts.optional,
		skipIf: eventKnown,
		// an event known by id (page context) needs no resolving; a typed name does
		next: opts.confirm ? (ctx) => (ctx.answers.eventId == null && ctx.answers.eventName ? 'resolveEvent' : resolveTarget(next, ctx)) : next,
	},
	...(opts.confirm
		? ([
				{ id: 'resolveEvent', type: 'backendAction', action: 'resolveEvent', onResult: { [SupportChatEventStatus.Found]: 'confirmEvent', '*': 'confirmEvent' }, onError: 'confirmEvent' },
				{
					id: 'confirmEvent',
					type: 'choice',
					field: 'eventConfirmed',
					textKey: confirmMatchText,
					skipIf: (ctx) => !isFuzzyMatch(ctx),
					next,
					options: [
						{ id: 'yes', labelKey: 'event.confirmMatchYes', value: true },
						{ id: 'no', labelKey: 'event.confirmMatchNo', value: false, set: { eventId: null, eventMatch: null, matchedEventName: null }, next: 'eventNameRetry' },
					],
				},
				{
					id: 'eventNameRetry',
					type: 'text',
					field: 'eventName',
					textKey: 'event.askNameAgain',
					placeholderKey: 'event.namePlaceholder',
					maxLength: 120,
					next: 'resolveEventRetry',
				},
				{ id: 'resolveEventRetry', type: 'backendAction', action: 'resolveEvent', onResult: { [SupportChatEventStatus.Found]: 'confirmEventRetry', '*': 'confirmEventRetry' }, onError: 'confirmEventRetry' },
				{
					id: 'confirmEventRetry',
					type: 'choice',
					field: 'eventConfirmed',
					textKey: confirmMatchText,
					skipIf: (ctx) => !isFuzzyMatch(ctx),
					next,
					options: [
						{ id: 'yes', labelKey: 'event.confirmMatchYes', value: true },
						{ id: 'no', labelKey: 'event.confirmMatchNo', value: false, set: { eventId: null, eventMatch: null, matchedEventName: null }, next: 'eventUnconfirmed' },
					],
				},
				{ id: 'eventUnconfirmed', type: 'message', textKey: 'event.unconfirmed', situationKey: 'sit.eventUnconfirmed', next: 'escEvent' },
			] as FlowStep[])
		: []),
];

const resolveTarget = (next: string | ((ctx: FlowContext) => string), ctx: FlowContext) => (typeof next === 'function' ? next(ctx) : next);

/**
 * Escalation flows: verify the order by e-mail first (exact order search across
 * all events, so support gets the order id), then ask for the event unless the
 * page / an earlier answer already provided it.
 */
const verifyOrderSteps = (next: string | ((ctx: FlowContext) => string)): FlowStep[] => [
	{
		id: 'verify',
		type: 'backendAction',
		action: 'lookup',
		textKey: 'tnr.lookingUp',
		onResult: { [SupportChatLookupStatus.EmailNotVerified]: 'sendCode', '*': 'event' },
		onError: 'event',
	},
	...emailCodeSteps('verify'),
	...eventSteps(next),
];

// --------------------------------------------------------------------------
// 1. Neprišla mi vstupenka
// --------------------------------------------------------------------------
/**
 * Owner decision (2026-09-05):
 *  e-mail → order search → PAID → resend the ticket.
 *  e-mail → not found → event → similar e-mail in the attendee list → support
 *           corrects the address and resends (escalation with full context).
 *  e-mail → found but NOT paid → "did the money leave your account?"
 *           yes → send the payment confirmation to support (manual check),
 *           no  → the order did not go through, place it again.
 * The event is asked only when the search needs it (nothing found / several
 * orders under the address).
 */
const ticketNotReceived: SupportFlow = {
	id: 'ticket-not-received',
	intent: 'ticket_not_received',
	titleKey: 'flow.ticketNotReceived',
	icon: '🎟️',
	priority: 10,
	enabled: true,
	audience: 'customer',
	entryStep: 'intro',
	steps: [
		{
			id: 'intro',
			type: 'message',
			// coming from "money left, ticket missing" the customer already said what happened
			textKey: (ctx) => (ctx.answers.moneyLeft !== undefined ? 'tnr.introFromPayment' : 'tnr.intro'),
			next: 'orderEmail',
		},
		{
			id: 'orderEmail',
			type: 'email',
			field: 'orderEmail',
			textKey: 'tnr.askOrderEmail',
			// owner decision: the code goes out right after the address; the lookup runs only once it is typed back
			next: 'sendCode',
		},
		{
			id: 'lookup',
			type: 'backendAction',
			action: 'lookup',
			textKey: 'tnr.lookingUp',
			onResult: {
				[SupportChatLookupStatus.EmailNotVerified]: 'sendCode',
				[SupportChatLookupStatus.PaidOrderFound]: 'resendOffer',
				[SupportChatLookupStatus.OrderFoundNotPaid]: 'notPaidInfo',
				[SupportChatLookupStatus.OrderFoundPendingTransfer]: 'pendingTransfer',
				[SupportChatLookupStatus.EventEnded]: 'eventEnded',
				[SupportChatLookupStatus.MultipleOrdersFound]: 'multiple',
				[SupportChatLookupStatus.NotFound]: 'notFound',
				'*': 'notFound',
			},
		},
		{
			id: 'resendOffer',
			type: 'choice',
			// several paid orders of the event → all of them go out at once, and the customer is told so
			textKey: (ctx) => (typeof ctx.answers.orderCount === 'number' && ctx.answers.orderCount > 1 ? 'tnr.paidFoundMany' : 'tnr.paidFound'),
			options: [
				{ id: 'resend', labelKey: 'tnr.resendButton', next: 'resend', variant: 'primary' },
				{ id: 'support', labelKey: 'tnr.contactSupportInstead', set: { situation: 'sit.tnr.supportInstead' }, next: 'supportQuestion' },
			],
		},
		{
			//the customer turned the automatic resend down - support needs to know what they actually want
			id: 'supportQuestion',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 2000,
			askMoreIfShort: 15,
			textKey: 'tnr.supportQuestion',
			placeholderKey: 'common.describePlaceholder',
			next: 'escEvent',
		},
		{
			id: 'resend',
			type: 'backendAction',
			action: 'resend',
			textKey: 'tnr.resending',
			onResult: { [SupportChatResendStatus.Sent]: 'resendDone', [SupportChatResendStatus.AlreadySent]: 'resendDone', '*': 'resendFailed' },
		},
		{
			id: 'resendDone',
			type: 'yesNo',
			field: 'resendHelped',
			textKeys: ['tnr.resendDone', 'tnr.helped'],
			options: [
				{ id: 'yes', labelKey: 'common.yes', next: 'resolved' },
				{ id: 'no', labelKey: 'common.no', next: 'resendNotHelped' },
			],
		},
		{ id: 'resolved', type: 'end', textKey: 'tnr.resolved', resolution: 'resolved' },
		{ id: 'resendNotHelped', type: 'message', textKey: 'tnr.notHelped', situationKey: 'sit.tnr.resendNotHelped', next: 'escEvent' },
		{ id: 'resendFailed', type: 'message', textKey: 'tnr.resendFailed', situationKey: 'sit.tnr.resendFailed', next: 'escEvent' },
		// --- found, but not paid
		{ id: 'notPaidInfo', type: 'message', textKey: 'tnr.notPaidFound', next: 'notPaidMoney' },
		{
			id: 'notPaidMoney',
			type: 'yesNo',
			field: 'moneyLeft',
			textKey: 'tnr.moneyQuestion',
			allowUnknown: true,
			// already answered when the customer came from the payment flow
			skipIf: (ctx) => ctx.answers.moneyLeft !== undefined,
			next: (ctx) => (ctx.answers.moneyLeft === false ? 'notPaidRepeat' : 'notPaidProof'),
		},
		{ id: 'notPaidProof', type: 'end', textKey: 'tnr.notPaidProof', resolution: 'human' },
		{ id: 'notPaidRepeat', type: 'end', textKey: 'tnr.notPaidRepeat', resolution: 'resolved' },
		// bank transfer: nothing to do, the order confirms itself when the money arrives
		{ id: 'pendingTransfer', type: 'end', textKey: 'tnr.pendingTransfer', resolution: 'resolved' },
		// event already ended: no resend
		{ id: 'eventEnded', type: 'end', textKey: 'tnr.eventEnded', resolution: 'resolved' },
		// --- nothing / too much found → narrow down by event
		// (a similar address in the attendee list is never revealed here: the server answers
		// not_found and hands the candidate to support in the escalation mail)
		{ id: 'multiple', type: 'message', textKey: 'tnr.multipleOrders', next: 'event' },
		{ id: 'notFound', type: 'message', textKey: 'tnr.notFound', next: 'event' },
		...eventSteps((ctx) => (ctx.answers.lookupStatus === SupportChatLookupStatus.MultipleOrdersFound ? 'lookup' : 'purchaseDate')),
		{
			id: 'purchaseDate',
			type: 'dateRange',
			field: 'purchaseRange',
			textKey: 'tnr.askPurchaseDate',
			options: [
				{ id: 'today', labelKey: 'date.today', next: 'lookupFallback' },
				{ id: 'yesterday', labelKey: 'date.yesterday', next: 'lookupFallback' },
				{ id: 'last7', labelKey: 'date.last7', next: 'lookupFallback' },
				{ id: 'older', labelKey: 'date.older', next: 'lookupFallback' },
				{ id: 'unknown', labelKey: 'common.dontKnow', next: 'lookupFallback' },
			],
		},
		{
			id: 'lookupFallback',
			type: 'backendAction',
			action: 'lookupFallback',
			textKey: 'tnr.lookingUpAgain',
			onResult: {
				[SupportChatLookupStatus.EmailNotVerified]: 'sendCode',
				[SupportChatLookupStatus.PaidOrderFound]: 'resendOffer',
				[SupportChatLookupStatus.OrderFoundNotPaid]: 'notPaidInfo',
				[SupportChatLookupStatus.OrderFoundPendingTransfer]: 'pendingTransfer',
				[SupportChatLookupStatus.EventEnded]: 'eventEnded',
				[SupportChatLookupStatus.NotFound]: 'stillNotFound',
				'*': 'stillNotFound',
			},
		},
		{ id: 'stillNotFound', type: 'message', textKey: 'tnr.stillNotFound', situationKey: 'sit.tnr.notFound', next: 'escEvent' },
		...emailCodeSteps('lookup'),
		...escalationTail('ticket_not_received', ['eventName', 'orderEmail', 'description', 'contactEmail', 'purchaseRange']),
	],
};

// --------------------------------------------------------------------------
// 2. Problém s platbou
// --------------------------------------------------------------------------
const paymentProblem: SupportFlow = {
	id: 'payment-problem',
	intent: 'payment_problem',
	titleKey: 'flow.paymentProblem',
	icon: '💳',
	priority: 20,
	enabled: true,
	audience: 'customer',
	entryStep: 'kind',
	steps: [
		{
			id: 'kind',
			type: 'choice',
			field: 'paymentIssue',
			textKey: 'pay.whatHappened',
			allowHuman: true,
			options: [
				{ id: 'charged_twice', labelKey: 'pay.chargedTwice', next: 'orderEmail' },
				{ id: 'failed', labelKey: 'pay.paymentFailed', next: 'failedInfo' },
				// the order search answers this one directly: transfer on its way / paid / nothing arrived
				{ id: 'transfer', labelKey: 'pay.bankTransfer', set: { moneyLeft: null }, next: 'flow:ticket-not-received' },
				{ id: 'refund_pending', labelKey: 'pay.refundPending', next: 'refundPendingInfo' },
				{ id: 'other', labelKey: 'pay.other', next: 'orderEmail' },
			],
		},
		{ id: 'failedInfo', type: 'message', textKey: 'pay.failedInfo', next: 'failedHelped' },
		// support template: refund timing + "check the original transaction, it may show as cancelled"
		{ id: 'refundPendingInfo', type: 'message', textKeys: ['pay.refundPendingInfo1', 'pay.refundPendingInfo2'], next: 'refundHelped' },
		// QA Z-03: a customer who still does not see the money needs a way forward, not only the review card
		{
			id: 'refundHelped',
			type: 'yesNo',
			field: 'refundHelped',
			textKey: 'pay.refundHelped',
			options: [
				{ id: 'yes', labelKey: 'common.yes', value: true, next: 'resolved' },
				{ id: 'no', labelKey: 'pay.refundStillMissing', value: false, set: { situation: 'sit.pay.refundMissing' }, next: 'orderEmail' },
			],
		},
		{
			id: 'failedHelped',
			type: 'yesNo',
			field: 'retryHelped',
			textKey: 'pay.failedTryAgain',
			options: [
				{ id: 'yes', labelKey: 'common.yes', next: 'resolved' },
				{ id: 'no', labelKey: 'pay.stillFailing', next: 'orderEmail' },
			],
		},
		{ id: 'resolved', type: 'end', textKey: 'common.gladToHelp', resolution: 'resolved' },
		{ id: 'orderEmail', type: 'email', field: 'orderEmail', textKey: 'common.askOrderEmail', next: 'sendCode' },
		//owner decision 2026-09-09: no order-number question - support finds the order by the verified e-mail
		...verifyOrderSteps('description'),
		{
			id: 'description',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 2000,
			askMoreIfShort: 15,
			textKey: 'pay.describe',
			placeholderKey: 'common.describePlaceholder',
			next: 'escEvent',
		},
		...escalationTail('payment_problem', ['paymentIssue', 'eventName', 'orderEmail', 'description', 'contactEmail']),
	],
};

// --------------------------------------------------------------------------
// 3. Potrebujem upraviť faktúru
// --------------------------------------------------------------------------
/**
 * Owner decision (Phase B): the invoice flow branches ONLY on whether the event
 * has invoicing enabled. Enabled → instructions before purchase, or collect
 * billing data for an existing order and escalate. Disabled → organizer issues
 * the invoice; the ticket itself is a valid accounting document ("cenina").
 */
const invoiceChange: SupportFlow = {
	id: 'invoice-change',
	intent: 'invoice_change',
	titleKey: 'flow.invoiceChange',
	icon: '🧾',
	priority: 30,
	enabled: true,
	audience: 'customer',
	entryStep: 'event',
	steps: [
		...eventSteps('invoicing', 'event.askNameNeutral', { confirm: true }),
		{
			//silent check (owner request): the customer only sees the outcome, not that a setting is being looked up
			id: 'invoicing',
			type: 'backendAction',
			action: 'eventInvoicing',
			onResult: { [SupportChatInvoicingStatus.Enabled]: 'haveOrder', [SupportChatInvoicingStatus.Disabled]: 'organizerContact', '*': 'eventNotFound' },
			onError: 'eventNotFound',
		},
		// QA FAK-02: an unknown event must not jump straight to the contact e-mail - say so and collect what to fix
		{ id: 'eventNotFound', type: 'message', textKey: 'inv.eventNotFound', situationKey: 'sit.inv.eventNotFound', next: 'describeFix' },
		{
			id: 'describeFix',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 2000,
			askMoreIfShort: 15,
			textKey: 'inv.askWhatToFix',
			placeholderKey: 'common.describePlaceholder',
			next: (ctx) => (ctx.answers.hasOrder === true ? 'invoiceData' : 'contactEmail'),
		},
		{
			id: 'haveOrder',
			type: 'choice',
			field: 'hasOrder',
			textKey: 'inv.haveOrder',
			options: [
				{ id: 'yes', labelKey: 'inv.haveOrderYes', value: true, next: 'orderEmail' },
				{ id: 'no', labelKey: 'inv.haveOrderNo', value: false, next: 'howToBuy' },
			],
		},
		{ id: 'howToBuy', type: 'end', textKeys: ['inv.howToBuy', 'inv.howToBuyTransfer'], resolution: 'resolved' },
		{
			id: 'organizerContact',
			type: 'backendAction',
			action: 'organizerContact',
			textKey: 'ref.findingOrganizer',
			onResult: { [SupportChatEventStatus.Found]: 'invoicingOff', '*': 'invoicingOffNoContact' },
			onError: 'invoicingOffNoContact',
		},
		{ id: 'invoicingOff', type: 'end', textKeys: ['inv.disabled', 'inv.disabledContact'], resolution: 'organizer' },
		{ id: 'invoicingOffNoContact', type: 'message', textKey: 'inv.disabled', next: 'organizerUnknown' },
		...noContactSteps('inv.disabledNoContact'),
		//the address is proven with a code before any order data is taken (owner request): the invoice
		//correction must come from the person who owns the order, not from anyone who knows the e-mail
		{ id: 'orderEmail', type: 'email', field: 'orderEmail', textKey: 'common.askOrderEmail', next: 'sendCode' },
		...emailCodeSteps('describeFix'),
		{
			id: 'invoiceData',
			type: 'form',
			field: 'invoiceData',
			textKey: 'inv.askNewData',
			fields: [
				{ name: 'company', labelKey: 'inv.company', required: true, maxLength: 120 },
				{ name: 'ico', labelKey: 'inv.ico', maxLength: 20 },
				{ name: 'dic', labelKey: 'inv.dic', maxLength: 20 },
				{ name: 'icdph', labelKey: 'inv.icdph', maxLength: 20 },
				{ name: 'street', labelKey: 'inv.street', required: true, maxLength: 120 },
				{ name: 'city', labelKey: 'inv.city', required: true, maxLength: 80 },
				{ name: 'zip', labelKey: 'inv.zip', required: true, maxLength: 12 },
				{ name: 'country', labelKey: 'inv.country', required: true, maxLength: 60 },
			],
			next: 'escEvent',
		},
		...escalationTail('invoice_change', ['eventName', 'orderEmail', 'description', 'invoiceData', 'contactEmail']),
	],
};

// --------------------------------------------------------------------------
// 4. Potrebujem zmeniť údaje
// --------------------------------------------------------------------------
const changeData: SupportFlow = {
	id: 'change-data',
	intent: 'change_data',
	titleKey: 'flow.changeData',
	icon: '✏️',
	priority: 40,
	// owner decision 2026-09-07: not in the main menu; kept for a later comeback
	enabled: false,
	audience: 'customer',
	entryStep: 'what',
	steps: [
		{
			id: 'what',
			type: 'choice',
			field: 'changeKind',
			textKey: 'chg.what',
			allowHuman: true,
			options: [
				// name on the ticket: event first – Silné reči get the self-service answer, everyone else goes to support
				{ id: 'holder', labelKey: 'chg.holderName', next: 'event' },
				{ id: 'email', labelKey: 'chg.email', next: 'orderEmail' },
				{ id: 'date', labelKey: 'chg.eventDate', next: 'organizerNote' },
				{ id: 'other', labelKey: 'chg.other', next: 'orderEmail' },
			],
		},
		{ id: 'holderInfo', type: 'message', textKey: 'chg.holderSelfService', next: 'holderHelped' },
		{
			id: 'holderHelped',
			type: 'yesNo',
			field: 'selfServiceHelped',
			textKey: 'common.didItHelp',
			options: [
				{ id: 'yes', labelKey: 'common.yes', next: 'resolved' },
				{ id: 'no', labelKey: 'common.no', next: 'orderEmail' },
			],
		},
		{ id: 'resolved', type: 'end', textKey: 'common.gladToHelp', resolution: 'resolved' },
		{ id: 'organizerNote', type: 'message', textKey: 'chg.dateIsOrganizer', situationKey: 'sit.chg.date', next: 'orderEmail' },
		{ id: 'orderEmail', type: 'email', field: 'orderEmail', textKey: 'common.askOrderEmail', next: 'sendCode' },
		...verifyOrderSteps((ctx) => {
			// holder path arrives here from the menu (no e-mail yet): decide self-service vs. support
			if (ctx.answers.changeKind === 'holder' && !ctx.answers.orderEmail) {
				return isSilneReci(ctx) ? 'holderInfo' : 'orderEmail';
			}
			return 'description';
		}),
		{
			id: 'description',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 2000,
			askMoreIfShort: 15,
			textKey: (ctx) =>
				ctx.answers.changeKind === 'holder' ? 'chg.describeHolder' : ctx.answers.changeKind === 'email' ? 'chg.describeEmail' : 'chg.describe',
			placeholderKey: 'common.describePlaceholder',
			next: 'escEvent',
		},
		...escalationTail('change_data', ['changeKind', 'eventName', 'orderEmail', 'description', 'contactEmail']),
	],
};

// --------------------------------------------------------------------------
// 4b. Otázka k eventu (program, vekové obmedzenie, parkovanie, ...)
// --------------------------------------------------------------------------
/**
 * Support cannot answer these (Inviton is only the seller) and always forwards
 * the customer to the organizer's contact address from the event - the bot
 * does exactly that, in one step.
 */
const eventQuestion: SupportFlow = {
	id: 'event-question',
	intent: 'event_question',
	titleKey: 'flow.eventQuestion',
	icon: '📅',
	priority: 45,
	// owner decision 2026-09-07: in the menu as the 8th bubble
	enabled: true,
	audience: 'customer',
	entryStep: 'intro',
	steps: [
		{ id: 'intro', type: 'message', textKey: 'evq.intro', next: 'event' },
		...eventSteps('organizerContact', 'event.askNameNeutral', { confirm: true }),
		{
			id: 'organizerContact',
			type: 'backendAction',
			action: 'organizerContact',
			textKey: 'ref.findingOrganizer',
			onResult: { [SupportChatEventStatus.Found]: 'organizerFound', '*': 'organizerUnknown' },
			onError: 'organizerUnknown',
		},
		{ id: 'organizerFound', type: 'end', textKey: 'evq.organizerEmail', resolution: 'organizer' },
		...noContactSteps('evq.organizerUnknown'),
		...escalationTail('event_question', ['eventName', 'contactEmail']),
	],
};

// --------------------------------------------------------------------------
// 4c. Zľavový kód alebo voucher
// --------------------------------------------------------------------------
/**
 * Codes and vouchers are configured by the organizer; the bot collects what
 * was entered and what happened, the event and a contact, and hands it over.
 */
const promoCode: SupportFlow = {
	id: 'promo-code',
	intent: 'promo_code',
	titleKey: 'flow.promoCode',
	icon: '🏷️',
	priority: 47,
	enabled: true,
	audience: 'customer',
	entryStep: 'intro',
	steps: [
		// owner decision: event first, then the code and what happened
		{ id: 'intro', type: 'message', textKey: 'promo.intro', next: 'event' },
		...eventSteps('describe', 'event.askNamePromo'),
		{
			id: 'describe',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 2000,
			askMoreIfShort: 15,
			textKey: 'promo.describe',
			placeholderKey: 'common.describePlaceholder',
			// the two cases support answers from a template are answered here; everything else goes to support
			next: (ctx) => (looksLikeTwoCodes(ctx.answers.description) ? 'twoCodes' : looksLikeBlockedCode(ctx.answers.description) ? 'blockedInfo' : 'escEvent'),
		},
		{ id: 'twoCodes', type: 'end', textKeys: ['promo.twoCodes1', 'promo.twoCodes2'], resolution: 'resolved' },
		{ id: 'blockedInfo', type: 'message', textKey: 'promo.blocked', next: 'blockedHelped' },
		{
			id: 'blockedHelped',
			type: 'yesNo',
			field: 'blockedHelped',
			textKey: 'common.didItHelp',
			options: [
				{ id: 'yes', labelKey: 'common.yes', next: 'resolved' },
				{ id: 'no', labelKey: 'common.no', next: 'stillNotWorking' },
			],
		},
		{ id: 'resolved', type: 'end', textKey: 'common.gladToHelp', resolution: 'resolved' },
		{ id: 'stillNotWorking', type: 'message', textKey: 'promo.stillNotWorking', situationKey: 'sit.promo.blocked', next: 'escEvent' },
		...escalationTail('promo_code', ['eventName', 'description', 'contactEmail']),
	],
};

// --------------------------------------------------------------------------
// 5. Storno / vrátenie peňazí
// --------------------------------------------------------------------------
/**
 * Owner decision (Phase B): Inviton never decides refunds. The bot explains
 * that the organizer of the event decides and then writes to Inviton, shows the
 * organizer's contact e-mail from the event, and falls back to the support
 * address when no contact is stored. No request is collected.
 */
const refund: SupportFlow = {
	id: 'refund',
	intent: 'refund',
	titleKey: 'flow.refund',
	icon: '↩️',
	priority: 50,
	enabled: true,
	audience: 'customer',
	entryStep: 'intro',
	steps: [
		{ id: 'intro', type: 'message', textKey: 'ref.intro', next: 'event' },
		...eventSteps('organizerContact', 'event.askName', { confirm: true }),
		{
			id: 'organizerContact',
			type: 'backendAction',
			action: 'organizerContact',
			textKey: 'ref.findingOrganizer',
			onResult: { [SupportChatEventStatus.Found]: 'organizerFound', '*': 'organizerUnknown' },
			onError: 'organizerUnknown',
		},
		{ id: 'organizerFound', type: 'end', textKey: 'ref.organizerEmail', resolution: 'organizer' },
		// no contact on the event → support finds it; the bot collects what the customer needs
		...noContactSteps('ref.organizerUnknown'),
		...escalationTail('refund', ['eventName', 'contactEmail']),
	],
};

// --------------------------------------------------------------------------
// 6. Som organizátor
// --------------------------------------------------------------------------
/**
 * Owner decision (Phase B): organizers get direct human contact — call the
 * support phone (InstanceConfig.Company.PhoneNumber) or write to the support
 * e-mail. No form, no escalation through the bot.
 */
const organizer: SupportFlow = {
	id: 'organizer',
	intent: 'organizer',
	titleKey: 'flow.organizer',
	icon: '👤',
	priority: 60,
	enabled: true,
	audience: 'organizer',
	entryStep: 'topic',
	steps: [
		// phone + e-mail first (owner decision), then "or tell me right here" – the request goes to the Inviton team
		{
			id: 'topic',
			type: 'choice',
			field: 'organizerTopic',
			textKeys: ['org.contactPhoneAndEmail', 'org.orWriteHere'],
			options: [
				{ id: 'quote', labelKey: 'org.topicQuote', next: 'describe' },
				{ id: 'problem', labelKey: 'org.topicProblem', next: 'orgEvent' },
				{ id: 'setup', labelKey: 'org.topicSetup', next: 'orgEvent' },
				{ id: 'services', labelKey: 'org.topicServices', next: 'describe' },
				{ id: 'other', labelKey: 'org.topicOther', next: 'describe' },
			],
		},
		{
			id: 'orgEvent',
			type: 'text',
			field: 'eventName',
			textKey: 'event.askNameNeutral',
			placeholderKey: 'event.namePlaceholder',
			maxLength: 120,
			skipIf: (ctx) => !!ctx.answers.eventName || !!ctx.contextEventName,
			next: 'describe',
		},
		{
			id: 'describe',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 3000,
			askMoreIfShort: 15,
			textKey: 'org.describe',
			placeholderKey: 'common.describePlaceholder',
			next: 'contactEmailAsk',
		},
		...escalationTail('organizer', ['organizerTopic', 'eventName', 'description', 'contactEmail'], false),
	],
};

// --------------------------------------------------------------------------
// 7. Iné / vlastná otázka — always last
// --------------------------------------------------------------------------
const other: SupportFlow = {
	id: 'other',
	intent: 'other',
	titleKey: 'flow.other',
	icon: '❓',
	priority: 1000,
	enabled: true,
	audience: 'both',
	entryStep: 'question',
	steps: [
		{
			id: 'question',
			type: 'text',
			field: 'description',
			multiline: true,
			maxLength: 3000,
			askMoreIfShort: 15,
			textKey: 'oth.askQuestion',
			placeholderKey: 'oth.placeholder',
			// the bot decides itself whether the question is about an event: on an event page it
			// confirms the page event, elsewhere it asks for the name only when the words say so
			next: (ctx) => (hasEventContext(ctx) || looksLikeEventQuestion(ctx.answers.description) ? 'event' : 'contactEmail'),
		},
		...eventSteps('contactEmail', 'event.askNameOptional', { optional: true }),
		{ id: 'contactEmail', type: 'email', field: 'contactEmail', textKey: 'contact.askEmail', next: 'confirm' },
		...escalationTail('other', ['description', 'eventName', 'contactEmail'], false).filter(
			(s) => s.id !== 'contactEmail' && s.id !== 'contactEmailAsk',
		),
	],
};

export const supportFlows: SupportFlow[] = [
	ticketNotReceived,
	paymentProblem,
	invoiceChange,
	changeData,
	eventQuestion,
	promoCode,
	refund,
	organizer,
	other,
];
